import React, { FunctionComponent } from 'react';
import { Grid, Radio, Button, Tooltip } from '@inplayer-org/inplayer-ui';
import styled from 'styled-components';
import { MdInfoOutline } from 'react-icons/md';

// hooks
import { useBoolean } from 'hooks/useBoolean';

// components
import StyledContainer from 'components/StyledContainer';

// styled components
import { BlueTypography } from 'components/SharedStyledElements';
import { useLocation } from 'react-router';

const { Container, Cell } = Grid;

const RadioWrapper = styled(Radio)`
  justify-content: flex-start;
`;

const TooltipWrapper = styled.div`
  display: flex;
  align-items: center;
  label,
  span {
    width: initial;
  }
`;

const MarginTooltip = styled(Tooltip)`
  margin-left: 0.3rem;
`;

const AccessTypesWrapper = styled(Cell)`
  margin: 0.5rem 0 0.5rem 2rem;
`;

interface OwnProps {
  accessControlType: string;
  setAccessControlType: (val: string) => void;
  onSubmitAssetControlType: () => void;
  onBackButtonClick: () => void;
}

type Props = OwnProps;

const AssetPriceForm: FunctionComponent<Props> = ({
  accessControlType,
  setAccessControlType,
  onSubmitAssetControlType,
  onBackButtonClick,
}) => {
  const { pathname } = useLocation();
  const isTemplate = pathname.includes('templates');

  const isLoginSelected = useBoolean(false);
  const isNoRegistrationSelected = useBoolean(false);

  const renderNonAuthAccessTypes = () => {
    return (
      <AccessTypesWrapper>
        <Container columns={1} rowGap="1rem">
          <Cell>
            <TooltipWrapper>
              <RadioWrapper
                tag="radiobutton_noregistration_passcode"
                id="code"
                name="code"
                value="code"
                label="Passcode"
                checked={accessControlType === 'code'}
                onChange={(e) => {
                  setAccessControlType(e.target.value);
                  if (isLoginSelected.value) isLoginSelected.toggle();
                }}
              />
              <MarginTooltip
                tag="tooltip_noregistration_passcode"
                content={`Customers access the content by only entering a pre-generated passcode. \n Available for paywall version V3 only.`}
                placement="right"
              >
                <MdInfoOutline />
              </MarginTooltip>
            </TooltipWrapper>
          </Cell>
        </Container>
      </AccessTypesWrapper>
    );
  };

  const renderLoginRegisterAccessTypes = () => {
    return (
      <AccessTypesWrapper>
        <Container columns={1}>
          <Cell>
            <TooltipWrapper>
              <RadioWrapper
                tag="radiobutton_registration_paid"
                id="paid"
                name="paid"
                value="paid"
                label="Paid"
                checked={accessControlType === 'paid'}
                onChange={(e) => setAccessControlType(e.target.value)}
              />
              <MarginTooltip
                tag="tooltip_registration_paid"
                content="Customers access the content by making an account and a purchase."
                placement="right"
              >
                <MdInfoOutline />
              </MarginTooltip>
            </TooltipWrapper>
          </Cell>
          <Cell>
            <TooltipWrapper>
              <RadioWrapper
                tag="radiobutton_registration_freemium"
                id="capture"
                name="capture"
                value="capture"
                label="Freemium"
                checked={accessControlType === 'capture'}
                onChange={(e) => setAccessControlType(e.target.value)}
              />
              <MarginTooltip
                tag="tooltip_registration_freemium"
                content="Customers access the content by just making an account. No purchase is needed."
                placement="right"
              >
                <MdInfoOutline />
              </MarginTooltip>
            </TooltipWrapper>
          </Cell>
          <Cell>
            <TooltipWrapper>
              <RadioWrapper
                tag="radiobutton_registration_accessvoucheronly"
                id="codeauth"
                name="codeauth"
                value="codeauth"
                label="Access voucher only"
                checked={accessControlType === 'codeauth'}
                onChange={(e) => setAccessControlType(e.target.value)}
              />
              <MarginTooltip
                tag="tooltip_registration_accessvoucheronly"
                content={`Customers access the content by creating an account and using an Access voucher. \n No other type of purchase is possible. Available for paywall version V3 only.`}
                placement="right"
              >
                <MdInfoOutline />
              </MarginTooltip>
            </TooltipWrapper>
          </Cell>
        </Container>
      </AccessTypesWrapper>
    );
  };

  return (
    <>
      <StyledContainer margin="2rem" columns={1}>
        <Cell>
          <Container columns={1} rowGap="1rem">
            <Cell>
              <BlueTypography variant="h5">Select access type</BlueTypography>
            </Cell>
            <Cell>
              <TooltipWrapper>
                <RadioWrapper
                  tag="radiobutton_registration"
                  id="login-register"
                  label="Registration"
                  checked={isLoginSelected.value}
                  onChange={() => {
                    isLoginSelected.toggle();
                    if (isNoRegistrationSelected.value) isNoRegistrationSelected.toggle();
                    setAccessControlType('');
                  }}
                  name="login"
                  value="login"
                />
                <MarginTooltip
                  tag="tooltip_registration"
                  content="Customers will have to register an account to access the content."
                  placement="right"
                >
                  <MdInfoOutline />
                </MarginTooltip>
              </TooltipWrapper>
              {isLoginSelected.value && renderLoginRegisterAccessTypes()}
            </Cell>
            <Cell>
              <TooltipWrapper>
                <RadioWrapper
                  tag="radiobutton_noregistration"
                  id="no-registration"
                  label="No registration"
                  checked={isNoRegistrationSelected.value}
                  onChange={() => {
                    isNoRegistrationSelected.toggle();
                    if (isLoginSelected.value) isLoginSelected.toggle();
                    setAccessControlType('code');
                  }}
                  name="noRegistration"
                  value="noRegistration"
                  disabled={isTemplate}
                />
                <MarginTooltip
                  tag="tooltip_noregistration"
                  content="Customers will access the content without registering an account."
                  placement="right"
                >
                  <MdInfoOutline />
                </MarginTooltip>
              </TooltipWrapper>
              {isNoRegistrationSelected.value && renderNonAuthAccessTypes()}
            </Cell>
          </Container>
        </Cell>
      </StyledContainer>
      <StyledContainer columns={3} margin="2rem 0 0 0">
        <Cell>
          <Button tag="button_back" fullWidth onClick={onBackButtonClick} buttonModifiers={['buttonLink']}>
            Back
          </Button>
        </Cell>
        <Cell left="3">
          <Button
            tag="button_next"
            fullWidth
            type="submit"
            disabled={!accessControlType}
            onClick={() => onSubmitAssetControlType()}
            buttonModifiers={['buttonPrimary']}
          >
            Next
          </Button>
        </Cell>
      </StyledContainer>
    </>
  );
};

export default AssetPriceForm;
